/**
 * Route-based PDP modes: URL path is the single source of truth for listing vs premium vs category.
 */

import { normalizeProjectSlugForApi } from './projectSlug';
import {
  buildProjectSeoSlugSegment,
  resolveSeoSlugInputsFromListingItem,
} from './projectSeoSlug';

export const PDP_MODE = {
  LISTING: 'listing',
  PREMIUM: 'premium',
  CATEGORY: 'category',
  MAPS: 'maps',
};

/** URL query value for map-context on maps PDP (`?mapcontext=location|nearby`). */
export const MAP_CONTEXT_LOCATION = 'location';
export const MAP_CONTEXT_NEARBY = 'nearby';

const LISTING_BASE = {
  apartment: '/allprojects',
  villa: '/allvillas',
  plot: '/allplots',
};

const PREMIUM_BASE = {
  apartment: '/premiumprojects',
  villa: '/premiumvillas',
  plot: '/premiumplots',
};

const CATEGORY_BASE = {
  apartment: '/categoryprojects',
  villa: '/categoryvillas',
  plot: '/categoryplots',
};

const MAPS_BASE = {
  apartment: '/mapsprojects',
  villa: '/mapvillas',
  plot: '/mapplots',
};

/** Legacy SEO paths — same premium experience as /premiumprojects family. */
const LEGACY_PREMIUM_PREFIX = [
  /^\/projects(?:\/|$)/,
  /^\/villa-projects(?:\/|$)/,
  /^\/plot-projects(?:\/|$)/,
];

const MODE_PREFIXES = [
  { mode: PDP_MODE.LISTING, bases: Object.values(LISTING_BASE) },
  { mode: PDP_MODE.PREMIUM, bases: Object.values(PREMIUM_BASE) },
  { mode: PDP_MODE.CATEGORY, bases: Object.values(CATEGORY_BASE) },
  { mode: PDP_MODE.MAPS, bases: Object.values(MAPS_BASE) },
];

function normalizePropertyType(propertyType) {
  const t = String(propertyType || 'apartment').toLowerCase();
  if (t === 'villa' || t === 'plot') return t;
  return 'apartment';
}

export function isPremiumPdpMode(mode) {
  return mode === PDP_MODE.PREMIUM;
}

export function isCategoryPdpMode(mode) {
  return mode === PDP_MODE.CATEGORY;
}

export function isListingPdpMode(mode) {
  return mode === PDP_MODE.LISTING;
}

export function isMapsPdpMode(mode) {
  return mode === PDP_MODE.MAPS;
}

/**
 * Nearby map PDP — continuation only (no builder / similar / shortlisted).
 * @param {object} [listingData] - remainingData or { safeProps: { search } }
 * @param {URLSearchParams|string} [searchParams]
 */
export function isMapsNearbyContext(listingData, searchParams) {
  const search = listingData?.safeProps?.search || listingData?.search || {};
  if (search.listingKind === 'nearby') return true;
  const qs =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(String(searchParams || '').replace(/^\?/, ''));
  return (qs.get('mapcontext') || '').trim().toLowerCase() === MAP_CONTEXT_NEARBY;
}

export function isMapsLocationContext(listingData, searchParams) {
  const search = listingData?.safeProps?.search || listingData?.search || {};
  if (search.listingKind === 'map') return true;
  const qs =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(String(searchParams || '').replace(/^\?/, ''));
  return (qs.get('mapcontext') || '').trim().toLowerCase() === MAP_CONTEXT_LOCATION;
}

/** Buy-home catalog pages — project cards open premium PDP (not location listing). */
const HOME_BUY_PATH = /^\/(?:apts-home|villas-home|plots-home)(?:\/|$)/;

export function isBuyHomePathname(pathname = '') {
  return HOME_BUY_PATH.test(String(pathname || '').split('?')[0]);
}

export function getPdpModeFromPathname(pathname = '') {
  const path = String(pathname || '').split('?')[0];

  for (const { mode, bases } of MODE_PREFIXES) {
    if (bases.some((base) => path.startsWith(`${base}/`) || path === base)) {
      return mode;
    }
  }

  if (LEGACY_PREMIUM_PREFIX.some((re) => re.test(path))) {
    return PDP_MODE.PREMIUM;
  }

  if (isBuyHomePathname(path)) {
    return PDP_MODE.PREMIUM;
  }

  return PDP_MODE.LISTING;
}

export function getPdpBasePath(propertyType = 'apartment', mode = PDP_MODE.LISTING) {
  const type = normalizePropertyType(propertyType);
  if (mode === PDP_MODE.PREMIUM) return PREMIUM_BASE[type];
  if (mode === PDP_MODE.CATEGORY) return CATEGORY_BASE[type];
  if (mode === PDP_MODE.MAPS) return MAPS_BASE[type];
  return LISTING_BASE[type];
}

/**
 * @param {object} [queryParams] - plain object or URLSearchParams
 * @param {object} [seoOptions] - { bhk, location, useSeoSlug?: boolean }
 */
export function buildProjectDetailPath(
  slug,
  propertyType = 'apartment',
  mode = PDP_MODE.LISTING,
  queryParams = null,
  seoOptions = null,
) {
  const safeSlug = normalizeProjectSlugForApi(slug) || String(slug || '').trim();
  if (!safeSlug) return '#';

  const opts = seoOptions && typeof seoOptions === 'object' ? seoOptions : {};
  const useSeoSlug = opts.useSeoSlug !== false;
  const pathSlug =
    useSeoSlug
      ? buildProjectSeoSlugSegment({
          slug: safeSlug,
          propertyType,
          bhk: opts.bhk,
          location: opts.location,
        }) || safeSlug
      : safeSlug;

  const base = getPdpBasePath(propertyType, mode);
  let path = `${base}/${pathSlug}`;

  if (!queryParams) return path;

  const qs =
    queryParams instanceof URLSearchParams
      ? queryParams
      : new URLSearchParams(
          Object.entries(queryParams).reduce((acc, [k, v]) => {
            if (v != null && String(v).trim() !== '') acc[k] = String(v).trim();
            return acc;
          }, {}),
        );

  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

/** Build PDP path from a listing card / API row (SEO slug when location is known). */
export function buildProjectDetailPathFromItem(
  item,
  propertyType = 'apartment',
  mode = PDP_MODE.LISTING,
  queryParams = null,
  extraSeoOptions = {},
) {
  const inputs = resolveSeoSlugInputsFromListingItem(item, propertyType);
  if (!inputs.slug) return '#';
  return buildProjectDetailPath(
    inputs.slug,
    propertyType,
    mode,
    queryParams,
    {
      bhk: extraSeoOptions.bhk ?? inputs.bhk,
      location: extraSeoOptions.location ?? inputs.location,
      useSeoSlug: extraSeoOptions.useSeoSlug,
    },
  );
}

/** Infer property type from pathname prefix when not passed explicitly. */
export function getPropertyTypeFromPathname(pathname = '') {
  const path = String(pathname || '');
  if (/\/(?:allvillas|premiumvillas|categoryvillas|mapvillas|villa-projects)(?:\/|$)/.test(path)) {
    return 'villa';
  }
  if (/\/(?:allplots|premiumplots|categoryplots|mapplots|plot-projects)(?:\/|$)/.test(path)) {
    return 'plot';
  }
  return 'apartment';
}
